import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Aperture, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

/**
 * La webcam dentro de la pagina, para Click Pic en la computadora (Carlos,
 * 30-oct). En una PC el navegador ignora capture="environment" y "Take photo"
 * abria el dialogo de archivos. En el telefono se sigue usando su camara, que
 * saca mejores fotos.
 *
 * Si la camara no arranca, deja de mandar imagen o la foto sale negra, se dice
 * por que y se ofrece elegir una foto: nunca queda una pantalla negra sin aviso.
 */

/** Una computadora: el puntero principal es un mouse y el navegador da la camara. */
export const esComputadora = () =>
  !!navigator.mediaDevices?.getUserMedia && !!window.matchMedia?.('(pointer: fine)').matches;

const BLOQUEADA = 'The browser is blocking the camera. Allow it for this site with the camera icon in the address bar and try again, or use Choose photo.';
const OCUPADA = 'Another app is using the camera. Close it and try again, or use Choose photo.';
const SIN_CAMARA = 'No camera was found. Connect one and try again, or use Choose photo.';
const SIN_IMAGEN = 'The camera is on but no picture is coming through. Try again, or use Choose photo.';
const CORTADA = 'The camera stopped. Try again, or use Choose photo.';
const FOTO_NEGRA = 'The photo came out black. Check that nothing is covering the camera and try again, or use Choose photo.';

const MOTIVOS = {
  NotAllowedError: BLOQUEADA,
  SecurityError: BLOQUEADA,
  NotReadableError: OCUPADA,
  AbortError: OCUPADA,
  NotFoundError: SIN_CAMARA,
  OverconstrainedError: SIN_CAMARA,
};

const ESPERA_IMAGEN_MS = 10000;

const pedido = (camara) => ({
  audio: false,
  video: {
    ...(camara ? { deviceId: { exact: camara } } : { facingMode: { ideal: 'environment' } }),
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
});

const apagar = (flujo) => flujo?.getTracks().forEach(t => t.stop());

// Brillo promedio de la foto achicada a 32x32: menos de 10 sobre 255 es negro.
const esNegra = (lienzo) => {
  const chico = document.createElement('canvas');
  chico.width = 32;
  chico.height = 32;
  const ctx = chico.getContext('2d');
  ctx.drawImage(lienzo, 0, 0, 32, 32);
  const { data } = ctx.getImageData(0, 0, 32, 32);
  let suma = 0;
  for (let i = 0; i < data.length; i += 4) suma += data[i] + data[i + 1] + data[i + 2];
  return suma / (32 * 32 * 3) < 10;
};

/**
 * Se monta al abrir y se desmonta al cerrar, asi la camara se apaga sola.
 * onFoto recibe un File JPEG, igual al que elige el comerciante.
 */
export default function CamaraEnVivo({ onFoto, onCerrar, onElegirFoto }) {
  const video = useRef(null);
  const [camara, setCamara] = useState(null);
  const [camaras, setCamaras] = useState([]);
  const [intento, setIntento] = useState(0);
  const [flujo, setFlujo] = useState(null);
  const [lista, setLista] = useState(false);
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    let vigente = true;
    let propio = null;
    setFlujo(null);
    setLista(false);
    setAviso('');
    navigator.mediaDevices.getUserMedia(pedido(camara))
      .then((s) => {
        if (!vigente) { apagar(s); return undefined; }
        propio = s;
        setFlujo(s);
        s.getVideoTracks()[0]?.addEventListener('ended', () => {
          if (vigente) { setLista(false); setAviso(CORTADA); }
        });
        // Las camaras recien se pueden listar con el permiso dado.
        return navigator.mediaDevices.enumerateDevices().then((ds) => {
          if (vigente) setCamaras(ds.filter(d => d.kind === 'videoinput' && d.deviceId).map(d => d.deviceId));
        }, () => {});
      })
      .catch((e) => {
        if (vigente) setAviso(MOTIVOS[e?.name] || 'The camera could not start. Try again, or use Choose photo.');
      });
    return () => { vigente = false; apagar(propio); };
  }, [camara, intento]);

  useEffect(() => {
    if (!flujo || lista || aviso) return undefined;
    const espera = setTimeout(() => setAviso(SIN_IMAGEN), ESPERA_IMAGEN_MS);
    return () => clearTimeout(espera);
  }, [flujo, lista, aviso]);

  // El video aparece con el dialogo y el flujo llega despues: se unen cuando estan los dos.
  const conectar = useCallback((el) => {
    video.current = el;
    if (el && flujo && el.srcObject !== flujo) el.srcObject = flujo;
  }, [flujo]);

  const capturar = () => {
    const v = video.current;
    if (!v?.videoWidth) { setAviso(SIN_IMAGEN); return; }
    const lienzo = document.createElement('canvas');
    lienzo.width = v.videoWidth;
    lienzo.height = v.videoHeight;
    lienzo.getContext('2d').drawImage(v, 0, 0);
    if (esNegra(lienzo)) { setAviso(FOTO_NEGRA); return; }
    setAviso('');
    lienzo.toBlob((blob) => {
      if (!blob) { setAviso('The photo could not be saved. Try again, or use Choose photo.'); return; }
      onFoto(new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const otraCamara = () => {
    const actual = flujo?.getVideoTracks()[0]?.getSettings().deviceId || camara;
    let i = (camaras.indexOf(actual) + 1) % camaras.length;
    // Si no se sabe cual esta abierta, al menos no pedir la misma que ya se pidio.
    if (camaras[i] === camara) i = (i + 1) % camaras.length;
    setCamara(camaras[i]);
  };

  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) onCerrar(); }}>
      <DialogContent className="max-w-2xl" data-testid="webcam-dialog">
        <DialogHeader>
          <DialogTitle>Take photo</DialogTitle>
          <DialogDescription>Hold the package in front of the camera so the whole box shows, then tap Capture.</DialogDescription>
        </DialogHeader>
        <div className="relative bg-slate-900 rounded-lg overflow-hidden aspect-video">
          <video
            ref={conectar}
            autoPlay
            playsInline
            muted
            onPlaying={() => { setLista(true); setAviso(''); }}
            className="w-full h-full object-contain"
            data-testid="webcam-video"
          />
          {!lista && !aviso && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-slate-300">
              <Loader2 className="w-4 h-4 animate-spin" /> Starting the camera…
            </div>
          )}
        </div>
        {aviso && (
          <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400" data-testid="webcam-aviso">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {aviso}
          </p>
        )}
        <DialogFooter className="flex-wrap gap-2 sm:gap-2">
          {camaras.length > 1 && (
            <Button type="button" variant="outline" onClick={otraCamara} data-testid="webcam-switch">
              <RefreshCw className="w-4 h-4 mr-2" /> Switch camera
            </Button>
          )}
          {aviso && (
            <Button type="button" variant="outline" onClick={onElegirFoto} data-testid="webcam-choose">
              Choose photo
            </Button>
          )}
          {aviso && !lista && (
            // Vuelve a la camara por defecto: la elegida con "Switch camera" puede ser la que se desconecto.
            <Button type="button" variant="outline" onClick={() => { setCamara(null); setIntento(n => n + 1); }} data-testid="webcam-retry">
              Try again
            </Button>
          )}
          <Button type="button" onClick={capturar} disabled={!lista} data-testid="webcam-capture">
            <Aperture className="w-4 h-4 mr-2" /> Capture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
